import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Info } from 'lucide-react';
import {
  fetchWeekly,
  fetchCommunities,
  fetchPortfolios,
  downloadWeeklyPdf,
  downloadWeeklyCsv,
  mapBackendClientId,
  type DashboardWeekly,
  type DashboardCommunity,
  type WeeklyCommunity,
} from '../services/dashboardApi';
import { TrendCharts, type TrendChartsTheme } from './TrendCharts';
import { LeadSourcesPanel } from './LeadSourcesPanel';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface WeeklyReportTheme {
  pageBg: string;
  cardBg: string;
  cardBorder: string;
  cardShadow: string;
  accent: string;
  /** Secondary accent — used for Tours line in trend charts */
  highlight?: string;
  sectionTitle: string;
  sectionDivider: string;
  textPrimary: string;
  textSecondary: string;
  textMuted: string;
  rowDivider: string;
  /** Positive WoW (more leads / more tours / lower FU rate) */
  positive: string;
  /** Negative WoW */
  negative: string;
}

export interface WeeklyReportProps {
  /** Frontend display id, e.g. 'morningstar-senior-living' */
  clientId: string;
  theme: WeeklyReportTheme;
  /**
   * Portfolio key the page-level selector has chosen (e.g. 'dwa-managed').
   * Empty string = no portfolio filter (e.g. for clients without portfolios).
   */
  portfolioKey?: string;
}

// ─── Formatters ─────────────────────────────────────────────────────────────

function fmt(n: number | null | undefined): string {
  return n != null ? n.toLocaleString() : '—';
}

function pct(n: number | null | undefined, signed = false): string {
  if (n == null) return '—';
  const v = (n * 100).toFixed(1);
  return signed && n > 0 ? `+${v}%` : `${v}%`;
}

function pp(n: number | null | undefined): string {
  if (n == null) return '—';
  const v = (n * 100).toFixed(1);
  return `${n > 0 ? '+' : ''}${v}pp`;
}

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
}

function fmtDateRange(start: string, end: string): string {
  // end is exclusive (next Mon 00:00); display as inclusive Sun
  const endInclusive = new Date(end);
  endInclusive.setDate(endInclusive.getDate() - 1);
  return `${fmtDate(start)} – ${fmtDate(endInclusive.toISOString())}`;
}

function communityLabel(c: WeeklyCommunity): string {
  return c.community_name || c.community_unique;
}

// ─── Sub-components ─────────────────────────────────────────────────────────

const MetricCard: React.FC<{
  label: string;
  value: string;
  sub?: string;
  subTone?: 'neutral' | 'positive' | 'negative';
  /** Data source platform shown in the top-right info tooltip. Defaults to
   *  'Aline' (every MorningStar metric today); pass explicitly as more
   *  sources are added so each box declares where its data comes from. */
  source?: string;
  theme: WeeklyReportTheme;
}> = ({ label, value, sub, subTone = 'neutral', source = 'Aline', theme }) => {
  const subColor =
    subTone === 'positive' ? theme.positive :
    subTone === 'negative' ? theme.negative :
    theme.textMuted;
  return (
    <div style={{
      background: theme.cardBg,
      border: `1px solid ${theme.cardBorder}`,
      boxShadow: theme.cardShadow,
      borderRadius: 4,
      padding: '1.25rem 1.5rem',
      position: 'relative',
    }}>
      {source && (
        <span
          title={`Source: ${source}`}
          aria-label={`Source: ${source}`}
          style={{
            position: 'absolute', top: '0.6rem', right: '0.6rem',
            display: 'inline-flex', color: theme.textMuted, cursor: 'help',
          }}
        >
          <Info size={14} />
        </span>
      )}
      <p style={{
        fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.15em',
        textTransform: 'uppercase', color: theme.textSecondary, marginBottom: '0.5rem',
        paddingRight: '1.25rem',
      }}>{label}</p>
      <p style={{
        fontSize: '1.8rem', fontWeight: 700, color: theme.textPrimary, lineHeight: 1,
      }}>{value}</p>
      {sub && (
        <p style={{ fontSize: '0.78rem', color: subColor, marginTop: '0.4rem', fontWeight: 500 }}>
          {sub}
        </p>
      )}
    </div>
  );
};

const SectionCard: React.FC<{
  title: string;
  children: React.ReactNode;
  theme: WeeklyReportTheme;
}> = ({ title, children, theme }) => (
  <div style={{
    background: theme.cardBg,
    border: `1px solid ${theme.cardBorder}`,
    boxShadow: theme.cardShadow,
    borderRadius: 4,
    padding: '1.25rem 1.5rem',
  }}>
    <h3 style={{
      fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.15em',
      textTransform: 'uppercase', color: theme.sectionTitle,
      marginBottom: '1rem', paddingBottom: '0.5rem',
      borderBottom: `1px solid ${theme.sectionDivider}`,
    }}>{title}</h3>
    {children}
  </div>
);

const TopList: React.FC<{
  items: WeeklyCommunity[];
  valueOf: (c: WeeklyCommunity) => string;
  emptyText: string;
  theme: WeeklyReportTheme;
}> = ({ items, valueOf, emptyText, theme }) => {
  if (!items.length) {
    return <p style={{ color: theme.textMuted, fontSize: '0.85rem' }}>{emptyText}</p>;
  }
  return (
    <ol style={{ margin: 0, padding: 0, listStyle: 'none' }}>
      {items.map((c, i) => (
        <li key={c.community_unique} style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
          padding: '0.4rem 0',
          borderBottom: i < items.length - 1 ? `1px solid ${theme.rowDivider}` : 'none',
        }}>
          <span style={{ color: theme.textPrimary, fontSize: '0.9rem' }}>
            <span style={{ color: theme.textMuted, marginRight: '0.5rem' }}>#{i + 1}</span>
            {communityLabel(c)}
          </span>
          <span style={{ color: theme.accent, fontWeight: 600, fontSize: '0.9rem' }}>
            {valueOf(c)}
          </span>
        </li>
      ))}
    </ol>
  );
};

// ─── Main component ─────────────────────────────────────────────────────────

export const WeeklyReportView: React.FC<WeeklyReportProps> = ({
  clientId, theme, portfolioKey = '',
}) => {
  const backendClientId = useMemo(() => mapBackendClientId(clientId), [clientId]);
  const [data, setData] = useState<DashboardWeekly | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadingKind, setDownloadingKind] = useState<'pdf' | 'csv' | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [communityFilter, setCommunityFilter] = useState<string>('ALL');
  const [communities, setCommunities] = useState<DashboardCommunity[]>([]);
  // Portfolio definitions are still needed to scope the community dropdown to
  // just members of the currently-selected portfolio.
  const [portfolioMembers, setPortfolioMembers] = useState<string[] | null>(null);

  // Communities scoped to the active portfolio.
  const scopedCommunities = useMemo(() => {
    if (!portfolioMembers) return communities;
    const allowed = new Set(portfolioMembers);
    return communities.filter(c => allowed.has(c.community_unique));
  }, [communities, portfolioMembers]);

  const requestOpts = useMemo(() => {
    const opts: { portfolio?: string; communityUnique?: string } = {};
    if (portfolioKey) opts.portfolio = portfolioKey;
    if (communityFilter !== 'ALL') opts.communityUnique = communityFilter;
    return opts;
  }, [portfolioKey, communityFilter]);

  const handleDownload = useCallback(async (kind: 'pdf' | 'csv') => {
    setDownloadingKind(kind);
    setDownloadError(null);
    try {
      if (kind === 'pdf') {
        await downloadWeeklyPdf(backendClientId, requestOpts);
      } else {
        await downloadWeeklyCsv(backendClientId, requestOpts);
      }
    } catch (e) {
      setDownloadError(e instanceof Error ? e.message : String(e));
    } finally {
      setDownloadingKind(null);
    }
  }, [backendClientId, requestOpts]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchWeekly(backendClientId, requestOpts);
      setData(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [backendClientId, requestOpts]);

  // Refetch on filter change
  useEffect(() => { load(); }, [load]);

  // One-shot fetch of community list for the dropdown (doesn't depend on filter)
  useEffect(() => {
    let cancelled = false;
    fetchCommunities(backendClientId)
      .then(rows => { if (!cancelled) setCommunities(rows); })
      .catch(() => { /* dropdown will just be empty */ });
    // Pull portfolio members so the community dropdown can be scoped to just
    // the active portfolio's communities (the portfolio selector itself lives
    // on the parent DashboardPage).
    fetchPortfolios(backendClientId)
      .then(rows => {
        if (cancelled) return;
        if (!portfolioKey) {
          setPortfolioMembers(null);
          return;
        }
        const def = rows.find(p => p.key === portfolioKey);
        setPortfolioMembers(def ? def.community_uniques : null);
      })
      .catch(() => { /* no scoping if we can't get portfolios */ });
    return () => { cancelled = true; };
  }, [backendClientId, portfolioKey]);

  // When portfolio changes upstream, reset community filter so we don't
  // accidentally keep a community that isn't in the new portfolio.
  useEffect(() => {
    setCommunityFilter('ALL');
  }, [portfolioKey]);

  if (loading && !data) {
    return <p style={{ color: theme.textMuted, padding: '2rem' }}>Loading weekly report…</p>;
  }
  if (error) {
    return <p style={{ color: theme.negative, padding: '2rem' }}>Error: {error}</p>;
  }
  if (!data || !data.snapshot_at) {
    return (
      <p style={{ color: theme.textMuted, padding: '2rem' }}>
        No snapshot data available yet. The daily Aline sync hasn't run, or the
        prospect snapshot is empty.
      </p>
    );
  }

  const t = data.totals;
  const tw = fmtDateRange(data.this_week.start, data.this_week.end);
  const pw = fmtDateRange(data.prior_week.start, data.prior_week.end);

  // Sub-tone semantics:
  //  - more leads / more tours WoW = good (positive)
  //  - lower FU rate (negative delta) is good — handled in the table via tdSigned()
  const leadsTone = t.leads_wow_pct == null ? 'neutral' : t.leads_wow_pct >= 0 ? 'positive' : 'negative';
  const toursTone = t.tours_wow_pct == null ? 'neutral' : t.tours_wow_pct >= 0 ? 'positive' : 'negative';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {/* Header */}
      <div style={{
        background: theme.cardBg, border: `1px solid ${theme.cardBorder}`,
        boxShadow: theme.cardShadow, borderRadius: 4, padding: '1rem 1.5rem',
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem',
      }}>
        <div>
          <p style={{
            fontSize: '0.72rem', letterSpacing: '0.1em', textTransform: 'uppercase',
            color: theme.textSecondary, fontWeight: 600, marginBottom: '0.35rem',
          }}>Weekly Operator Report</p>
          <p style={{ fontSize: '0.95rem', color: theme.textPrimary }}>
            <strong title="This Week (Mon-Sun)">{tw}</strong>
            <span style={{ color: theme.textMuted }}> vs </span>
            <strong title="Prior Week (Mon-Sun)">{pw}</strong>
          </p>
          <p style={{ fontSize: '0.7rem', color: theme.textMuted, marginTop: '0.25rem' }}>
            Snapshot: {new Date(data.snapshot_at).toLocaleString()}
          </p>
          <div style={{ marginTop: '0.6rem', display: 'flex', alignItems: 'center', gap: '0.45rem' }}>
            <label
              htmlFor="weekly-community-filter"
              style={{ fontSize: '0.7rem', color: theme.textSecondary, fontWeight: 600,
                       letterSpacing: '0.08em', textTransform: 'uppercase' }}
            >
              Community
            </label>
            <select
              id="weekly-community-filter"
              value={communityFilter}
              onChange={e => setCommunityFilter(e.target.value)}
              style={{
                fontSize: '0.78rem', padding: '0.3rem 0.55rem', borderRadius: 3,
                border: `1px solid ${theme.cardBorder}`, background: theme.cardBg,
                color: theme.textPrimary, fontFamily: 'inherit', cursor: 'pointer',
                minWidth: 220,
              }}
            >
              <option value="ALL">All Communities ({scopedCommunities.length})</option>
              {scopedCommunities.map(c => (
                <option key={c.community_unique} value={c.community_unique}>
                  {c.name || c.community_unique}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.4rem' }}>
          <div style={{ display: 'flex', gap: '0.4rem' }}>
            <button
              onClick={() => handleDownload('pdf')}
              disabled={downloadingKind !== null}
              style={{
                fontSize: '0.78rem',
                fontWeight: 600,
                padding: '0.45rem 1rem',
                borderRadius: 4,
                cursor: downloadingKind !== null ? 'default' : 'pointer',
                fontFamily: 'inherit',
                background: theme.accent,
                color: '#ffffff',
                border: `1px solid ${theme.accent}`,
                opacity: downloadingKind !== null ? 0.6 : 1,
                whiteSpace: 'nowrap',
              }}
            >
              {downloadingKind === 'pdf' ? 'Generating…' : '⬇ PDF'}
            </button>
            <button
              onClick={() => handleDownload('csv')}
              disabled={downloadingKind !== null}
              style={{
                fontSize: '0.78rem',
                fontWeight: 600,
                padding: '0.45rem 1rem',
                borderRadius: 4,
                cursor: downloadingKind !== null ? 'default' : 'pointer',
                fontFamily: 'inherit',
                background: 'transparent',
                color: theme.accent,
                border: `1px solid ${theme.accent}`,
                opacity: downloadingKind !== null ? 0.6 : 1,
                whiteSpace: 'nowrap',
              }}
            >
              {downloadingKind === 'csv' ? 'Generating…' : '⬇ CSV'}
            </button>
          </div>
          {downloadError && (
            <p style={{ fontSize: '0.7rem', color: theme.negative, maxWidth: 280, textAlign: 'right' }}>
              {downloadError}
            </p>
          )}
        </div>
      </div>

      {/* Current-state cards — occupancy + active leads + move-ins this week.
          Sits above the period-comparison row so users see "where we are now"
          before "how the period compares." */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
        <MetricCard
          label="Occupancy Rate"
          value={pct(data.current_state.occupancy_rate)}
          sub={`${fmt(data.current_state.occupied_units)} / ${fmt(data.current_state.total_units)} units`}
          theme={theme}
        />
        <MetricCard
          label="Active Leads"
          value={fmt(data.current_state.active_leads)}
          sub="status = Active"
          theme={theme}
        />
        <MetricCard
          label="Move-ins This Week"
          value={fmt(data.current_state.move_ins_in_window)}
          sub="gross"
          theme={theme}
        />
        <MetricCard
          label="Move-outs This Week"
          value={fmt(data.current_state.move_outs_in_window)}
          sub="gross"
          theme={theme}
        />
        <MetricCard
          label="Net Change This Week"
          value={
            data.current_state.net_move_change > 0
              ? `+${data.current_state.net_move_change}`
              : fmt(data.current_state.net_move_change)
          }
          sub="move-ins − move-outs"
          subTone={
            data.current_state.net_move_change > 0
              ? 'positive'
              : data.current_state.net_move_change < 0
                ? 'negative'
                : 'neutral'
          }
          theme={theme}
        />
      </div>

      {/* Period-comparison cards — leads + tours TW vs PW */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
        <MetricCard
          label="Leads This Week"
          value={fmt(t.leads_tw)}
          sub={t.leads_wow_pct != null ? `${pct(t.leads_wow_pct, true)} WoW` : 'no prior data'}
          subTone={leadsTone}
          theme={theme}
        />
        <MetricCard
          label="Leads Prior Week"
          value={fmt(t.leads_pw)}
          sub="reference"
          theme={theme}
        />
        <MetricCard
          label="Tours This Week"
          value={fmt(t.tours_tw)}
          sub={t.tours_wow_pct != null ? `${pct(t.tours_wow_pct, true)} WoW` : 'no prior data'}
          subTone={toursTone}
          theme={theme}
        />
        <MetricCard
          label="Tours Prior Week"
          value={fmt(t.tours_pw)}
          sub="reference"
          theme={theme}
        />
      </div>

      {/* Trend charts — leads/tours, FU rate, occupancy over time */}
      <TrendCharts
        backendClientId={backendClientId}
        portfolio={portfolioKey || undefined}
        communityUnique={communityFilter !== 'ALL' ? communityFilter : undefined}
        theme={{
          cardBg: theme.cardBg,
          cardBorder: theme.cardBorder,
          cardShadow: theme.cardShadow,
          sectionTitle: theme.sectionTitle,
          sectionDivider: theme.sectionDivider,
          textPrimary: theme.textPrimary,
          textSecondary: theme.textSecondary,
          textMuted: theme.textMuted,
          accent: theme.accent,
          highlight: theme.highlight || theme.accent,
          negative: theme.negative,
        } as TrendChartsTheme}
      />

      {/* 4 top-N panels — hidden when filtered to a single community
          (the lists wouldn't make sense — backend returns them empty). */}
      {communityFilter === 'ALL' && (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1rem' }}>
        <SectionCard title="Top Communities — Leads" theme={theme}>
          <TopList
            items={data.top_leads}
            valueOf={c => `${c.leads_tw} leads`}
            emptyText="No leads this week."
            theme={theme}
          />
        </SectionCard>
        <SectionCard title="Top Communities — Tours" theme={theme}>
          <TopList
            items={data.top_tours}
            valueOf={c => `${c.tours_tw} tours`}
            emptyText="No tours this week."
            theme={theme}
          />
        </SectionCard>
        <SectionCard title="Biggest Leads Gain WoW" theme={theme}>
          <TopList
            items={data.biggest_gain}
            valueOf={c => `${c.leads_wow_delta > 0 ? '+' : ''}${c.leads_wow_delta} leads WoW`}
            emptyText="No movement."
            theme={theme}
          />
        </SectionCard>
        <SectionCard title="Biggest Leads Decline WoW" theme={theme}>
          <TopList
            items={data.biggest_decline}
            valueOf={c => `${c.leads_wow_delta} leads WoW`}
            emptyText="No declines."
            theme={theme}
          />
        </SectionCard>
      </div>
      )}

      {/* Lead source breakdown — same period scope, prior-period delta */}
      <LeadSourcesPanel
        clientId={clientId}
        theme={theme}
        period="week"
        portfolioKey={portfolioKey}
        communityUnique={communityFilter !== 'ALL' ? communityFilter : undefined}
      />

      {/* Community breakdown table */}
      <SectionCard
        title={communityFilter === 'ALL' ? 'Community Breakdown' : 'Community Detail'}
        theme={theme}
      >
        <p style={{ fontSize: '0.75rem', color: theme.textMuted, marginBottom: '0.75rem' }}>
          <strong>TW</strong> = This Week · <strong>PW</strong> = Prior Week ·
          {' '}<strong>WoW</strong> = Week-over-Week % change · {' '}
          <strong>FU Rate</strong> = Financially Unqualified % of leads ·
          {' '}<strong>Δ pp</strong> = delta in percentage points. Higher FU rate = more
          unqualified leads (unfavorable).
        </p>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ background: 'transparent', borderBottom: `2px solid ${theme.sectionDivider}` }}>
                <th style={th(theme, 'left')}>Community</th>
                <th style={th(theme)} title="Leads created this week (Mon-Sun)">Leads TW</th>
                <th style={th(theme)} title="Leads created in the prior week">Leads PW</th>
                <th style={th(theme)} title="Week-over-week % change in leads">Leads WoW</th>
                <th style={th(theme)} title="Tours scheduled this week">Tours TW</th>
                <th style={th(theme)} title="Tours scheduled the prior week">Tours PW</th>
                <th style={th(theme)} title="Week-over-week % change in tours">Tours WoW</th>
                <th style={th(theme)} title="Financially Unqualified % of leads this week">FU Rate TW</th>
                <th style={th(theme)} title="Financially Unqualified % of leads prior week">FU Rate PW</th>
                <th style={th(theme)} title="FU rate change in percentage points (lower is better)">FU Δ</th>
              </tr>
            </thead>
            <tbody>
              {data.communities.map(c => (
                <tr key={c.community_unique} style={{ borderBottom: `1px solid ${theme.rowDivider}` }}>
                  <td style={td(theme, 'left')}>{communityLabel(c)}</td>
                  <td style={td(theme)}>{c.leads_tw}</td>
                  <td style={td(theme)}>{c.leads_pw}</td>
                  <td style={tdSigned(theme, c.leads_wow_pct, 'leads')}>{pct(c.leads_wow_pct, true)}</td>
                  <td style={td(theme)}>{c.tours_tw}</td>
                  <td style={td(theme)}>{c.tours_pw}</td>
                  <td style={tdSigned(theme, c.tours_wow_pct, 'tours')}>{pct(c.tours_wow_pct, true)}</td>
                  <td style={td(theme)}>{pct(c.fu_rate_tw)}</td>
                  <td style={td(theme)}>{pct(c.fu_rate_pw)}</td>
                  <td style={tdSigned(theme, c.fu_rate_delta_pp, 'fu')}>{pp(c.fu_rate_delta_pp)}</td>
                </tr>
              ))}
              <tr style={{ background: theme.cardBg, borderTop: `2px solid ${theme.sectionDivider}`, fontWeight: 600 }}>
                <td style={td(theme, 'left')}>Total / Avg</td>
                <td style={td(theme)}>{t.leads_tw}</td>
                <td style={td(theme)}>{t.leads_pw}</td>
                <td style={tdSigned(theme, t.leads_wow_pct, 'leads')}>{pct(t.leads_wow_pct, true)}</td>
                <td style={td(theme)}>{t.tours_tw}</td>
                <td style={td(theme)}>{t.tours_pw}</td>
                <td style={tdSigned(theme, t.tours_wow_pct, 'tours')}>{pct(t.tours_wow_pct, true)}</td>
                <td style={td(theme)}>{pct(t.fu_rate_tw)}</td>
                <td style={td(theme)}>{pct(t.fu_rate_pw)}</td>
                <td style={tdSigned(theme, t.fu_rate_delta_pp, 'fu')}>{pp(t.fu_rate_delta_pp)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </SectionCard>
    </div>
  );
};

// ─── Table style helpers ────────────────────────────────────────────────────

function th(theme: WeeklyReportTheme, align: 'left' | 'right' = 'right'): React.CSSProperties {
  return {
    padding: '0.5rem 0.5rem',
    textAlign: align,
    fontSize: '0.7rem',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: theme.textSecondary,
    fontWeight: 600,
    whiteSpace: 'nowrap',
  };
}

function td(theme: WeeklyReportTheme, align: 'left' | 'right' = 'right'): React.CSSProperties {
  return {
    padding: '0.45rem 0.5rem',
    textAlign: align,
    color: theme.textPrimary,
    whiteSpace: 'nowrap',
  };
}

function tdSigned(
  theme: WeeklyReportTheme,
  v: number | null | undefined,
  kind: 'leads' | 'tours' | 'fu',
): React.CSSProperties {
  let color: string = theme.textPrimary;
  if (v != null) {
    if (kind === 'fu') {
      // Lower FU rate = positive
      color = v <= 0 ? theme.positive : theme.negative;
    } else {
      color = v >= 0 ? theme.positive : theme.negative;
    }
  }
  return { ...td(theme), color, fontWeight: 500 };
}

export default WeeklyReportView;
